import { Axios } from "api/axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const otp = location.state?.otp;

  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!email || !otp) {
      toast.error("Invalid access. Please verify OTP first.");
      navigate("/forgot-password");
    }
  }, [email, otp, navigate]);

  const validation = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Password must contain at least 1 uppercase letter")
        .matches(/[0-9]/, "Password must contain at least 1 number")
        .matches(
          /[!@#$%^&*(),.?":{}|<>]/,
          "Password must contain at least 1 special character"
        )
        .required("Please Enter Your New Password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          email: email,
          new_password: values.password,
          otp: otp, // Include OTP if your backend requires it
        };

        const res: any = await Axios.post(`api/user/reset-password/`, payload);

        if (res?.message || res?.data?.message) {
          toast.success("Password reset successfully!");
          setTimeout(() => navigate("/login"), 1500);
        } else {
          toast.error("Password reset failed");
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || "Failed to reset password"
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="col-xxl-6 mx-auto">
      <Card className="card mb-0 border-0 shadow-none mb-0">
        <Card.Body className="p-sm-5 m-lg-4">
          <div className="text-center mt-2">
            <h5 className="fs-3xl">Create New Password</h5>
            <p className="text-muted mb-4">
              Your new password must be different from previously used passwords
            </p>
          </div>

          <Alert
            className="border-0 alert-success text-center mb-2 mx-2"
            role="alert"
          >
            OTP verified for: <strong>{email}</strong>
          </Alert>

          <div className="p-2">
            <Form onSubmit={validation.handleSubmit}>
              <div className="mb-3">
                <Form.Label className="form-label">New Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={passwordShow ? "text" : "password"}
                    placeholder="Enter new password"
                    name="password"
                    value={validation.values.password}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    isInvalid={
                      validation.touched.password &&
                      !!validation.errors.password
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {validation.errors.password}
                  </Form.Control.Feedback>
                  <Button
                    variant="link"
                    className="position-absolute end-0 top-0 text-decoration-none text-muted"
                    type="button"
                    onClick={() => setPasswordShow(!passwordShow)}
                    style={{ zIndex: 2 }}
                  >
                    <i
                      className={
                        passwordShow ? "ri-eye-fill" : "ri-eye-off-fill"
                      }
                    ></i>
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  Must be at least 8 characters with uppercase, number, and
                  special character
                </Form.Text>
              </div>

              <div className="mb-3">
                <Form.Label className="form-label">
                  Confirm New Password
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={confirmPasswordShow ? "text" : "password"}
                    placeholder="Confirm new password"
                    name="confirmPassword"
                    value={validation.values.confirmPassword}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    isInvalid={
                      validation.touched.confirmPassword &&
                      !!validation.errors.confirmPassword
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {validation.errors.confirmPassword}
                  </Form.Control.Feedback>
                  <Button
                    variant="link"
                    className="position-absolute end-0 top-0 text-decoration-none text-muted"
                    type="button"
                    onClick={() => setConfirmPasswordShow(!confirmPasswordShow)}
                    style={{ zIndex: 2 }}
                  >
                    <i
                      className={
                        confirmPasswordShow ? "ri-eye-fill" : "ri-eye-off-fill"
                      }
                    ></i>
                  </Button>
                </div>
              </div>

              <div className="text-center mt-4">
                <Button
                  className="btn btn-primary w-100"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Resetting Password...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </div>

              <div className="mt-4 text-center">
                <p className="mb-0">
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-primary text-decoration-underline"
                  >
                    Back to Login
                  </Link>
                </p>
              </div>
            </Form>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ResetPassword;
